import { useContext, useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react';
import { StateMark } from '@/components/brand/StateMark';
import { LEAVE_MS, useLoadingGate } from '@/components/brand/threshold';
import { EmptyState, cn, type EmptyStateAction } from '@/components/ui';
import { PaneContext, WorkspaceContext } from './workspaceContext';

/*
  StateFrame — вкладка целиком, когда её содержимого нет (R42 «Знак ждёт за рамку, заглушка — за данные»):
  загрузка при первом за сеанс открытии, не найдено, нет доступа, не открылось, сбой вкладки. Вид —
  EmptyState frame="page" с медальоном знака (StateMark), текст и действия — от вызывающего.

  Загрузка идёт по порогам (useLoadingGate): первые 400 мс пусто; потом медальон — лепесток на пройденный
  шаг, строка шага с 1,5 с, секунды с 3 с, «Дольше обычного» и действия с 10 с. На «готово» веер
  досаживается, медальон уходит, содержимое (children) входит. Сбой на шаге: веер стоит на достигнутом,
  кольцо медальона краснеет и толстеет; «Повторить» — шаги идут дальше с того же медальона.

  Голосом объявляется немногое: шаг на 3 с, «дольше обычного» на 10 с и сбой. Состояние, пришедшее в
  видимую вкладку окна в фокусе, получает фокус на заголовке.
*/

export type StateFrameKind = 'loading' | 'missing' | 'locked' | 'failed' | 'crashed';

interface StateFrameProps {
  kind: StateFrameKind;
  title: string;
  description?: ReactNode;
  /** Шаги загрузки — для loading и failed. */
  steps?: readonly string[];
  /** Сколько шагов пройдено. */
  done?: number;
  /** Сбой: на каком шаге и почему (kind="failed"). */
  failed?: { step?: string; reason: string };
  /**
   * У загрузки действия появляются только «дольше обычного», и первое из них — «Повторить»: новая попытка,
   * пороги идут с нуля от нажатия.
   */
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /** Подробности для поддержки: код, имя и текст ошибки. */
  details?: string;
  /** Содержимое вкладки: входит, когда загрузка дошла до конца. */
  children?: ReactNode;
  className?: string;
}

export function StateFrame({
  kind,
  title,
  description,
  steps = [],
  done = 0,
  failed,
  action,
  secondaryAction,
  details,
  children,
  className,
}: StateFrameProps) {
  const total = steps.length;
  const loading = kind === 'loading';
  // «Повторить» — новая попытка (после сбоя или «дольше обычного»): пороги с нуля от нажатия, медальон стоит
  // на месте. Иначе после долгого сбоя сразу «Дольше обычного · 22 с», отсчитанное от первой попытки.
  const [attempt, setAttempt] = useState(0);
  const [wasFailed, setWasFailed] = useState(kind === 'failed');
  if (wasFailed !== (kind === 'failed')) {
    setWasFailed(kind === 'failed');
    if (loading) setAttempt((a) => a + 1);
  }
  const retry =
    loading && action
      ? {
          ...action,
          onClick: () => {
            setAttempt((a) => a + 1);
            action.onClick?.();
          },
        }
      : action;
  // Сбой — тоже ожидание: после «Повторить» шаги идут дальше, а медальон не пропадает на новый порог.
  const gate = useLoadingGate((loading || kind === 'failed') && done < total, undefined, attempt);

  // Досадка кончилась — медальон уходит поверх входящего содержимого.
  const [leaving, setLeaving] = useState(false);
  const [entering, setEntering] = useState(false);
  const [wasShown, setWasShown] = useState(gate.shown);
  if (wasShown !== gate.shown) {
    setWasShown(gate.shown);
    if (!gate.shown && loading) {
      setLeaving(true);
      setEntering(true);
    }
  }
  useEffect(() => {
    if (!leaving) return undefined;
    const t = window.setTimeout(() => setLeaving(false), LEAVE_MS);
    return () => window.clearTimeout(t);
  }, [leaving]);

  // Фокус на заголовок — только в видимой вкладке окна, в котором работают. Не из-под набора текста и не
  // с переключателей (сегменты, вкладки): там человек ещё выбирает, и стрелки должны идти дальше.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const ws = useContext(WorkspaceContext);
  const pane = useContext(PaneContext);
  const focusHeading = useEffectEvent(() => {
    const h = headingRef.current;
    if (!h || !ws || ws.state.focused !== pane || h.offsetParent === null || !document.hasFocus()) return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active.closest('input, textarea, [contenteditable="true"], [role="tablist"]')
    )
      return;
    h.focus({ preventScroll: true });
  });
  useEffect(() => {
    if (kind !== 'loading') focusHeading();
  }, [kind]);

  const at = Math.min(done, Math.max(0, total - 1));
  const step = total > 0 ? `Шаг ${at + 1} из ${total} · ${steps[at]}` : '';
  const status =
    loading && gate.named ? (gate.counting && !gate.slow ? `${step} · ${gate.seconds} с` : step) : '';
  const reason = failed && (failed.step ? `Шаг «${failed.step}»: ${failed.reason}` : failed.reason);
  const frame = (
    <EmptyState
      frame="page"
      mark={<StateMark kind={kind} done={Math.min(done, total)} total={total} />}
      headingRef={headingRef}
      title={title}
      description={
        kind === 'failed' && reason
          ? reason
          : loading && gate.slow
            ? `Дольше обычного · ${gate.seconds} с`
            : description
      }
      status={loading ? status : undefined}
      action={loading && !gate.slow ? undefined : retry}
      secondaryAction={loading && !gate.slow ? undefined : secondaryAction}
      details={details}
    />
  );

  if (loading && !gate.shown) {
    // Первые 400 мс — пусто: короткое ожидание ничего не показывает.
    if (done < total) return <div aria-busy="true" className={cn('h-full', className)} />;
    return (
      <div className={cn('relative h-full', className)}>
        <div className={cn('h-full', entering && 'animate-fade-in')}>{children}</div>
        {leaving && (
          <div aria-hidden className="pointer-events-none absolute inset-0 animate-fade-out">
            {frame}
          </div>
        )}
      </div>
    );
  }

  // Голос: на 3 с и на 10 с — по разу, без шагов и секунд (иначе читалка говорит без остановки); сбой — сразу.
  const spoken = !loading
    ? ''
    : gate.slow
      ? `${title}: дольше обычного`
      : gate.counting
        ? `${title}: идёт загрузка`
        : '';
  const alarm = kind === 'failed' || kind === 'crashed';
  // Занятость — только у самой рамки и только пока идут шаги: голос стоит рядом, а не внутри неё, — иначе
  // читалка вправе придержать его до конца загрузки.
  return (
    <div className={cn('h-full animate-fade-in', className)}>
      <div aria-busy={loading || undefined} className="h-full">
        {frame}
      </div>
      <span role="status" className="sr-only">
        {spoken}
      </span>
      {alarm && (
        <span role="alert" className="sr-only">
          {kind === 'failed' && reason ? `${title}. ${reason}` : title}
        </span>
      )}
    </div>
  );
}
